import {
    useState,
    useRef,
    useEffect,
    createContext,
    type HTMLAttributes,
    type Dispatch,
    type SetStateAction,
    useCallback
} from 'react';
import styled from 'styled-components';
import { SvgSprite } from 'modules/svgSprite';
import ValidatedInput from 'modules/react-input';
import { objectToPascalCase, format } from 'modules/helpers/strings';
import ReactButton, { ButtonStyle } from 'modules/react-button';
import { colors, breakpoint, sizes } from 'variables';
import { fetchJSON, formatParams, extractParams } from 'modules/helpers/fetch';
import { getUrlParam, replaceUrlParam } from 'modules/helpers';
import mediator from 'modules/mediator';
import device from 'modules/helpers/device';
import {
    type AddressCleansingSuggestion,
    type FindStoreResult,
    type ParcelShopSearchData,
    type StoreParcelShopResult,
    ValidateUrl
} from 'autogen/swagger/all';
import { useTranslation } from 'react-i18next';
import { NoResultsDidYouMeanText, ShowMore } from 'autogen/translation-keys/trans-website-checkout-deliverynudging';
import { Spacing16, Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import InlineButton, { InlineButtonWithIcon } from 'components/generic/InlineButton';
import { NoSearchText } from 'autogen/translation-keys/trans-website-checkout-deliverynudging-choosesuppliermodal';
import {
    CouldNotGetLocation,
    NoResultText,
    SearchAddress,
    ShopsNearSearch,
    FindStore as FindStoreTrans,
    UseMyLocation
} from 'autogen/translation-keys/trans-website-myprofile-store';
import Icon from 'components/Icon';

const addressQueryParam = 'addressQuery';
const sharedMarginY = '5px';
const sharedMarginX = '10px';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ChildContainer = styled.div`
    width: 100%;
`;

const Form = styled.form`
    display: flex;
    margin: 0 -${sharedMarginX};
    flex-wrap: wrap;
    flex-shrink: 0;
    width: 100%;
    margin-bottom: ${Spacing16};

    ${breakpoint.up(sizes.sm)} {
        flex-wrap: nowrap;
    }
`;

const SearchFieldWrapper = styled.div`
    flex: 1 1 100%;
    margin: ${sharedMarginY} 0;

    ${breakpoint.up(sizes.sm)} {
        flex: 1 0 50%;
        margin-right: ${sharedMarginX};
    }
`;

const InputWrapper = styled.div``;

const SearchActions = styled.div`
    flex: 1 1 100%;
    margin: ${sharedMarginY} 0;
    gap: ${Spacing8};

    ${breakpoint.up(sizes.sm)} {
        flex: 1 0 150px;
        margin-left: ${sharedMarginX};

        &:before {
            /* basically a padding-top to match the search fields height, done this way because of em/rem to px rounding errors */
            content: '‏‏‎ ‎'; /* rlm; - used as a invisible character  */
            display: block;
            opacity: 0;
            margin-bottom: 5px;
        }
    }
`;

const SearchButton = styled(ReactButton)`
    width: 100%;
    margin-bottom: ${Spacing8};
`;

const UsePositionButton = styled(InlineButtonWithIcon)`
    display: flex;
    margin-left: auto;
    margin-right: auto;
`;

const ListHeadline = styled.div`
    font-size: 12px;
`;

const NoSearchWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 30px 0;
`;
const NoSearchIcon = styled(SvgSprite)`
    width: 90px;
    height: 90px;
    fill: ${colors.color2};
    margin-bottom: 10px;
`;
const NoSearchLabel = styled.div`
    font-weight: bold;
    font-size: 18px;
    max-width: 400px;
    text-align: center;
`;

const ShowMoreButton = styled(ReactButton)`
    margin-top: 20px;
`;

const DidYouMeanButton = styled(InlineButton)`
    margin-top: ${Spacing8};
    margin-bottom: ${Spacing4};
`;

function formatDataSuggestionAddress(suggestion: AddressCleansingSuggestion): string {
    return `${suggestion.address}, ${suggestion.zipCode} ${suggestion.city}`;
}

interface IFindStore extends HTMLAttributes<HTMLDivElement> {
    stores?: StoreParcelShopResult[];
    addressQuery?: string;
    productId?: number | string;
    showStock?: boolean;
    updateUrl?: boolean;
    endpoints?: {
        searchStores: string;
        getGeolocation: string;
    };
    closeModalCallback: () => void;
}

export interface IFindStoreContext extends IFindStore {
    searchData: { current: ParcelShopSearchData };
    setCurrentPage: Dispatch<SetStateAction<number>>;
    incrementPage: () => void;
}

export const FindStoreContext = createContext<IFindStoreContext>(null);

export const FindStore = (props: IFindStore): JSX.Element => {
    const {
        addressQuery = '',
        stores: initialStores = [],
        productId,
        showStock,
        updateUrl = false,
        endpoints = {
            searchStores: '/internal/store/searchstorebyaddress',
            getGeolocation: '/internal/store/searchstorebycoordinates'
        },
        closeModalCallback,
        children
    } = props;
    const searchQuery = useRef(addressQuery);
    const [stores, setStores] = useState<any>(initialStores);
    const searchData = useRef<ParcelShopSearchData>(undefined);
    const [storeNearText, setStoreNearText] = useState<string>();
    const inputRef = useRef(null);
    const [isLastPage, setIsLastPage] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [dataCleansingSuggestion, setDataCleansingSuggestion] = useState<AddressCleansingSuggestion>();
    const { t } = useTranslation();

    const getGeolocation = (): Promise<any> => {
        $.loadingSpinner(window, true);

        return new Promise((resolve, reject): void => {
            navigator.geolocation.getCurrentPosition(resolve, (): void => {
                $.loadingSpinner(window, false);
                mediator.publish('growl', {
                    message: t(CouldNotGetLocation),
                    remove: 2000
                });
                reject();
            });
        });
    };

    const updateStoreNearText = (searchValue: string): void =>
        searchValue ? setStoreNearText(format(t(ShopsNearSearch))(searchValue)) : null;

    const search = (url, isTextSearch = false): void => {
        $.loadingSpinner(window, true);
        fetchJSON(encodeURI(url))
            .then((res: FindStoreResult): void => {
                if (res.searchData?.addressQuery && !isTextSearch) {
                    inputRef.current.setInputValue(res.searchData.addressQuery);
                }

                if (res.searchData?.dataCleansingSuggestion) {
                    if (res.searchData.dataCleansingSuggestion.isExactMatch) {
                        inputRef.current.setInputValue(
                            formatDataSuggestionAddress(res.searchData.dataCleansingSuggestion)
                        );
                    } else {
                        setDataCleansingSuggestion(res.searchData.dataCleansingSuggestion);
                    }
                } else {
                    setDataCleansingSuggestion(undefined);
                }

                setStores([...res.parcelShops]);

                setIsLastPage(res.searchData?.isLastPage);

                updateStoreNearText(res.searchData.addressQuery);
                searchData.current = res.searchData;

                if (updateUrl) {
                    const newUrl = replaceUrlParam(
                        window.location.href,
                        addressQueryParam,
                        res.searchData.addressQuery
                    );
                    window.history.replaceState(null, null, newUrl);
                }
                $.loadingSpinner(window, false);
            })
            .catch((error) => console.error(error));
    };

    const searchStores = useCallback(
        (query: string): void => {
            const [base, params] = extractParams(endpoints.searchStores);
            const data: any = {
                ...params,
                addressQuery: query,
                page: currentPage
            };
            if (productId) {
                data.productId = productId;
            }
            search(`${base}${formatParams(data)}`, true);
        },
        [currentPage]
    );

    const searchStoresByCoordinates = useCallback(
        async (position?: any): Promise<void> => {
            const [base, params] = extractParams(endpoints.getGeolocation);
            const {
                coords: { latitude, longitude }
            } = position || (await getGeolocation());
            const data: any = {
                ...params,
                latitude,
                longitude,
                page: currentPage
            };
            if (productId) {
                data.productId = productId;
            }
            search(`${base}${formatParams(data)}`);
        },
        [currentPage]
    );

    const onValueChange = (val: string): void => {
        searchQuery.current = val;
    };

    const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        if (!searchQuery.current) return;
        searchStores(searchQuery.current);
    };

    function incrementPage(): void {
        const _currentPage = searchData ? searchData.current.page : currentPage;
        setCurrentPage(_currentPage + 1);
    }

    function onDidYouMeanClick() {
        const newAddress = formatDataSuggestionAddress(dataCleansingSuggestion);
        inputRef.current.setInputValue(newAddress);
        searchStores(newAddress);
    }

    // Effect for subsequent searches
    useEffect((): void => {
        if (searchData.current) {
            const { searchType, addressQuery: query, coordinates: coords } = searchData.current;

            if (searchType === 'AddressQuery') {
                searchStores(query);
            }

            if (searchType === 'Coordinates') {
                searchStoresByCoordinates({ coords });
            }
        }
    }, [currentPage, searchStores, searchStoresByCoordinates]);

    // Effect for initial search
    useEffect((): void => {
        if (!searchQuery.current) {
            const querySearch = getUrlParam(window.location.search, addressQueryParam)[0];
            if (querySearch) {
                inputRef.current.setInputValue(querySearch);
                searchStores(querySearch);
            } else if (breakpoint.max(sizes.md)) {
                searchStoresByCoordinates();
            }
        } else {
            searchStores(searchQuery.current);
        }
    }, []);

    useEffect((): void => {
        if (addressQuery) {
            inputRef.current.setInputValue(addressQuery);
            searchStores(addressQuery);
        }
    }, [addressQuery]);

    const onKeyUp = (event): void => {
        if (event.key === 'Enter' && device.isMobile) {
            event.target.blur();
        }
    };

    return (
        <FindStoreContext.Provider
            value={{
                stores,
                showStock,
                incrementPage,
                searchData,
                endpoints,
                setCurrentPage,
                closeModalCallback
            }}
        >
            <Container>
                <Form onSubmit={onFormSubmit}>
                    <SearchFieldWrapper>
                        <InputWrapper>
                            <ValidatedInput
                                label={t(SearchAddress)}
                                type="text"
                                validateUrl={ValidateUrl}
                                field="addressQuery"
                                validationState={0}
                                name="addressQuery"
                                onValueChange={onValueChange}
                                onKeyUp={onKeyUp}
                                ref={inputRef}
                            />
                        </InputWrapper>
                        {!!storeNearText && <ListHeadline>{storeNearText}</ListHeadline>}
                        {!!dataCleansingSuggestion && (
                            <DidYouMeanButton type="button" variant="dark" size="M" onClick={onDidYouMeanClick}>
                                {t(NoResultsDidYouMeanText, {
                                    suggestedAddress: formatDataSuggestionAddress(dataCleansingSuggestion)
                                })}
                            </DidYouMeanButton>
                        )}
                    </SearchFieldWrapper>
                    <SearchActions>
                        <SearchButton type="submit" buttonStyle={ButtonStyle.primary}>
                            {t(FindStoreTrans)}
                        </SearchButton>
                        <UsePositionButton
                            type="button"
                            icon={<Icon type="Pin" size={16} />}
                            onClick={(): Promise<void> => searchStoresByCoordinates()}
                        >
                            {t(UseMyLocation)}
                        </UsePositionButton>
                    </SearchActions>
                </Form>

                <ChildContainer>{children}</ChildContainer>

                {!!searchQuery?.current?.length && !stores?.length && (
                    <NoSearchWrapper>
                        <NoSearchLabel>{t(NoResultText)}</NoSearchLabel>
                    </NoSearchWrapper>
                )}
                {!isLastPage && !!stores?.length && (
                    <ShowMoreButton type="button" buttonStyle={ButtonStyle.secondary} onClick={incrementPage}>
                        {t(ShowMore)}
                    </ShowMoreButton>
                )}
                {!storeNearText && !stores?.length && (
                    <NoSearchWrapper>
                        <NoSearchIcon sprite="MapsPinOutline" />
                        <NoSearchLabel>{t(NoSearchText)}</NoSearchLabel>
                    </NoSearchWrapper>
                )}
            </Container>
        </FindStoreContext.Provider>
    );
};

export default FindStore;
